import { DateRangeKey, InternalTabKey } from '../../types/internal';

interface InternalDashboardHeaderProps {
    activeTab: InternalTabKey;
    onTabChange: (tab: InternalTabKey) => void;
    dateRange: DateRangeKey;
    onDateRangeChange: (range: DateRangeKey) => void;
    onRefresh: () => void;
    isLoading: boolean;
}

export const InternalDashboardHeader: React.FC<InternalDashboardHeaderProps> = ({
    activeTab,
    onTabChange,
    dateRange,
    onDateRangeChange,
    onRefresh,
    isLoading
}) => {
    return (
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-neutral-200/60 px-6 py-3">
            <div className="max-w-[1600px] mx-auto flex items-center justify-between">
                <div className="flex items-center gap-8">
                    <div>
                        <h1 className="text-sm font-semibold text-neutral-900">Internal Overview</h1>
                        <p className="text-[10px] text-neutral-500">Platform-wide performance & metrics</p>
                    </div>

                    <nav className="flex items-center gap-1 bg-neutral-100/50 p-1 rounded-xl border border-neutral-200/40">
                        {([
                            { key: 'overview', label: 'Platform Overview' },
                            { key: 'automation', label: 'System Automation & Growth' },
                            { key: 'infrastructure', label: 'Systems Infrastructure' }
                        ] as const).map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => onTabChange(tab.key)}
                                className={`px-4 py-1.5 text-[11px] font-bold uppercase tracking-tight rounded-lg transition-all ${activeTab === tab.key
                                    ? 'bg-neutral-900 text-white shadow-md shadow-neutral-200'
                                    : 'text-neutral-500 hover:text-neutral-700 hover:bg-neutral-200/50'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                <div className="flex items-center gap-3">
                    <div className="flex items-center bg-slate-100 p-1 rounded-lg">
                        {(['7d', '30d', '90d'] as const).map((range) => (
                            <button
                                key={range}
                                onClick={() => onDateRangeChange(range)}
                                className={`px-3 py-1 text-[10px] font-medium rounded-md transition-all ${dateRange === range
                                    ? 'bg-white text-neutral-900 shadow-sm'
                                    : 'text-neutral-500 hover:text-neutral-700'
                                    }`}
                            >
                                {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
                            </button>
                        ))}
                    </div>

                    <button
                        onClick={onRefresh}
                        disabled={isLoading}
                        className="p-2 text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors disabled:opacity-50"
                        title="Refresh Data"
                    >
                        <svg className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </button>
                </div>
            </div>
        </header>
    );
};
